import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useI18n } from "@/i18n";
import { useTheme } from "@/contexts/ThemeContext";
import { BetaBadge, BetaNotice } from "@/components/BetaBadge";
import { openCookieSettings } from "@/components/CookieConsent";

export function Footer() {
  const { t } = useI18n();
  const dark = useTheme()?.isDark;
  const legalLinks = [
    { to: "/about", key: "footer.about" },
    { to: "/how-it-works", key: "footer.howItWorks" },
    { to: "/editorial-policy", key: "footer.editorial" },
    { to: "/ai-transparency", key: "footer.aiTransparency" },
    { to: "/copyright-policy", key: "footer.copyright" },
    { to: "/terms", key: "footer.terms" },
    { to: "/privacy-policy", key: "footer.privacy" },
    { to: "/contact", key: "footer.contact" },
    { to: "/advertise", key: "footer.advertise" },
  ];
  return (
    <footer className="mt-24 border-t border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2"><Logo size={40} /><BetaBadge /></div>
            <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-violet-600">{t("footer.poweredBy")}</p>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed max-w-sm">{t("footer.rights")}</p>
            <BetaNotice className="mt-4 max-w-sm" />
          </div>
          <div className="text-sm">
            <div className="font-mono text-[10px] uppercase tracking-widest text-violet-600 mb-3">{t("footer.legal")}</div>
            <ul className="space-y-2 text-slate-600">
              {legalLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.to.slice(1)}`} className="hover:text-violet-700">{t(l.key)}</Link>
                </li>
              ))}
              <li><button type="button" data-testid="footer-cookie-settings" onClick={openCookieSettings} className="hover:text-violet-700">{t("consent.settings")}</button></li>
            </ul>
          </div>
          <div className="text-sm">
            <div className="font-mono text-[10px] uppercase tracking-widest text-violet-600 mb-3">{t("footer.platform")}</div>
            <ul className="space-y-2 text-slate-600">
              <li><Link to="/latest" className="hover:text-violet-700">{t("section.latest")}</Link></li>
              <li><Link to="/category/generative-ai" className="hover:text-violet-700">{t("cat.generative-ai")}</Link></li>
              <li><Link to="/category/ai-regulation" className="hover:text-violet-700">{t("cat.ai-regulation")}</Link></li>
              <li><Link to="/admin" data-testid="admin-dashboard-link" className="hover:text-violet-700">{t("footer.adminPortal")}</Link></li>
            </ul>
          </div>
        </div>

        {/* Operated by Trust Ed Techno */}
        <div data-testid="footer-company" className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <a href="https://trustedtechno.com" target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-3 group">
            <img data-testid="footer-company-logo" src={dark ? "/logo-dark.png" : "/logo.png"} alt="Trust Ed Techno" className="h-11 w-11 object-contain" />
            <span className="text-sm">
              <span className="block text-[11px] text-muted-foreground">{t("footer.product")}</span>
              <span className="block font-display font-bold text-slate-800 group-hover:text-violet-700 transition-colors">Trust Ed Techno</span>
            </span>
          </a>
          <p data-testid="footer-ai-disclaimer" className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed sm:ms-auto sm:max-w-md">
            {t("footer.aiGenerated")}
          </p>
        </div>

        <div data-testid="ai-disclosure" className="mt-6 pt-6 border-t border-slate-200">
          <p className="text-xs text-muted-foreground leading-relaxed">{t("footer.disclosure")}</p>
          <p className="mt-3 text-xs text-slate-500">
            {t("brand.name")} — {t("brand.tagline")} · © {new Date().getFullYear()} Trust Ed Techno for Development and Innovation LLC, Abu Dhabi, UAE. · <a href="https://nxttechnews.com" data-testid="footer-site-url" className="text-violet-600 hover:underline" dir="ltr">nxttechnews.com</a>
          </p>
        </div>
      </div>
    </footer>
  );
}
